import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Button,
  Input,
  Heading
} from '@chakra-ui/react';
import Pagination from '../../components/Pagination';
import { getAllUsers, updateUser } from '../../../../services/adminService';
import { useToast } from '@chakra-ui/react';
import { useUser } from '@clerk/clerk-react';
import DeleteUserButton from './DeleteUserButton';

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [editedUsers, setEditedUsers] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const { user: clerkUser } = useUser();

  const toast = useToast();

  useEffect(() => {
    // Fetch users when the component mounts
    const fetchUsers = async () => {
      const data = await getAllUsers();
      console.log(data);
      if (data) {
        setUsers(data);
        const teacherUsers = data.filter(user => user.role === 'teacher');
        console.log(teacherUsers);
        setTeachers(teacherUsers);
      } else {
        console.error('Unexpected data format: ', data);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      const filtered = users.filter(user => {
        const lowerFilter = searchTerm.toLowerCase();
        return (
          (user.name && user.name.toLowerCase().includes(lowerFilter)) ||
          (user.email && user.email.toLowerCase().includes(lowerFilter))
        );
      });
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [searchTerm, users]);


  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handleChange = (clerkId, field, value) => {
    setEditedUsers(prev => ({
      ...prev,
      [clerkId]: {
        ...prev[clerkId],
        [field]: value,
      },
    }));
  }

  const handleSave = async (clerkId) => {
    const userChanges = editedUsers[clerkId];
    const adminId = clerkUser.id;
    
    if (userChanges) {
      const updatedUserChanges = { ...userChanges, clerkId };
      const response = await updateUser(adminId, updatedUserChanges);
      if (response.updated) {
        toast({
          title: 'User updated.',
          description: 'User information has been updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.clerkId === clerkId ? { ...user, ...userChanges } : user
          )
        );
        setEditedUsers(prev => {
          const newEdits = { ...prev };
          delete newEdits[clerkId];
          return newEdits;
        });
      } else {
        toast({
          title: 'Update failed.',
          description: 'There was an error updating the user information.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <Box p={4}>
      <Heading>Users</Heading>
      <Box mb={4}>
        <Input
          placeholder="Search by username or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="md"
        />
      </Box>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Username</Th>
            <Th>Email</Th>
            <Th>Authorization</Th>
            <Th>Student of</Th>
            <Th>Role</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {currentUsers.length > 0 ? (
            currentUsers.map((user) => (
              <Tr key={user.clerkId}>
                <Td fontWeight={700}>{user.name}</Td>
                  <Td>
                    <Input 
                      value={editedUsers[user.clerkId]?.email || user.email}
                      onChange={(e) => handleChange(user.clerkId, 'email', e.target.value)}
                      size="md"
                      />
                  </Td>
                <Td>
                  <Select
                    value={editedUsers[user.clerkId]?.authorization || user.authorization}
                    onChange={(e) => handleChange(user.clerkId, 'authorization', e.target.value)}
                    paddingRight="0.8rem"
                  >
                    <option value="student">Student</option>
                    <option value="admin">Admin</option>
                  </Select>
                </Td>
                <Td>
                  <Select
                    value={editedUsers[user.clerkId]?.teacherName  || user.teacherName}
                    onChange={(e) => {
                      const selectedTeacher = teachers.find(teacher => teacher.name === e.target.value);
                      if (selectedTeacher) {
                        handleChange(user.clerkId, 'teacher', {
                          teacherName: selectedTeacher.name, 
                          teacherUserId: selectedTeacher.userId,
                          teacherEmail: selectedTeacher.email
                        });
                      }
                    }}
                      placeholder="Select a teacher"
                      paddingRight="0.8rem"
                    >
                    {teachers.map((teacher) => (
                      <option key={teacher.clerkId} value={teacher.name}>
                        {teacher.name}
                      </option>
                    ))}
                  </Select>
                </Td>
                <Td>
                  <Select
                    value={editedUsers[user.clerkId]?.role || user.role}
                    onChange={(e) => handleChange(user.clerkId, 'role', e.target.value)}
                    paddingRight="0.8rem"
                  >
                    <option value="visitor">Visitor</option>
                    <option value="student">Student</option>
                    <option value="premium">Premium</option>
                    <option value="teacher">Teacher</option>
                  </Select>
                </Td>
                <Td>
                  <Button
                    colorScheme="blue"
                    onClick={() => handleSave(user.clerkId)}
                    isDisabled={!editedUsers[user.clerkId]}
                    ml={2}
                  >
                    Save
                  </Button>
                  <DeleteUserButton 
                  clerkId={user.clerkId}
                  username={user.name}
                  adminId={clerkUser.id}/>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan="6">No users found.</Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default UsersList;
